// Two-character sentinels: a Unicode noncharacter paired with a semicolon.
// Lexical's markdown emphasis parser only recognises ASCII punctuation, so
// the semicolon ensures ** and _ adjacent to sentinels are parsed correctly.
// The noncharacter ensures the pair never appears in real content.

import {
  $createRangeSelection,
  $getNodeByKey,
  $getRoot,
  $isTextNode,
  $nodesOfType,
  type NodeKey,
  TextNode,
} from 'lexical';

import {
  $dismissSuggestionMark,
  $removeSuggestionMarkWithContent,
  $setSuggestionMarkSource,
  $sourceSuggestionMark,
  $wrapSelectionInSuggestionMark,
  SuggestionMarkNode,
} from '../nodes/SuggestionMarkNode';
import {
  $dismissSuggestion,
  $removeSuggestionWithContent,
  $setSuggestionSource,
  $sourceSuggestion,
  SuggestionNode,
} from '../nodes/SuggestionNode';
import { $removeEmptyListItems, $removeEmptyTableColumns, $removeEmptyTableRows } from '../utils';
import { Source } from '../types';
import { isValidSource, parseAttributes } from '../config/transformers/suggestion';
import { TABLE_ROW_REG_EXP, TABLE_ROW_DIVIDER_REG_EXP } from '../config/transformers/table';
import { PAGE_BREAK_LINE_RE, PAGE_BREAK_MARKER } from '../config/transformers/pagebreak';

const SENTINEL_START = '\uFDD0;';
const SENTINEL_END = ';\uFDD1';

const FORMATTED_SPACE_RE = /(\*{1,3}|_{1,3}|~~)&#32;\1/g;

function stripFormattedSpaces(markdown: string): string {
  return markdown.replace(FORMATTED_SPACE_RE, ' ');
}

type SuggestionSpan = {
  id: string;
  source: Source;
  tooltip: string | null;
};

export function preprocessMarkdown(markdown: string): {
  markdown: string;
  spans: SuggestionSpan[];
} {
  let md = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  md = normalizeInlineSuggestions(md);
  md = markOrphanParents(md);
  md = stripFormattedSpaces(md);
  md = extractPageBreaksFromSuggestions(md);
  const { processed, spans } = replaceSuggestionTagsWithSentinels(md);
  md = stripDeletionFormatting(processed, spans);
  return { markdown: md, spans };
}

export function $postProcessImportedTree(spans: SuggestionSpan[]): void {
  $applySuggestionMarks(spans);
  $removeAllSentinels();
  $clearDeletionMarkFormatting();
  $stripOrphanedMarkersFromTree();
}

function replaceSuggestionTagsWithSentinels(markdown: string): {
  processed: string;
  spans: SuggestionSpan[];
} {
  const spans: SuggestionSpan[] = [];
  let counter = 0;

  // Non-greedy match without the `s` flag so it only catches single-line
  // (inline) suggestions. Multi-line block suggestions pass through
  // untouched for the block transformer.
  const processed = markdown.replace(
    /<suggestion([^>]*)>(.*?)<\/suggestion>/g,
    (_match, attrString: string, content: string) => {
      const id = `suggestion-mark-${counter++}`;
      const attrs = parseAttributes(attrString);
      const source: Source = isValidSource(attrs.source) ? attrs.source : 'default';
      const tooltip = attrs.tooltip || null;
      spans.push({ id, source, tooltip });
      return `${SENTINEL_START}${content}${SENTINEL_END}`;
    },
  );

  return { processed: hoistListMarkerSpace(processed), spans };
}

const BROKEN_LIST_MARKER_RE = /^(\s*(?:[-*+]|\d+\.))(\uFDD0;)( +)/gm;

function hoistListMarkerSpace(processed: string): string {
  return processed.replace(BROKEN_LIST_MARKER_RE, '$1 $2');
}

function $applySuggestionMarks(spans: SuggestionSpan[]): void {
  const boundaries = $collectBoundaries(spans.length);

  for (let i = 0; i < boundaries.length; i++) {
    const b = boundaries[i];
    if (!b) continue;
    if (b.anchor.nodeKey === b.focus.nodeKey && b.anchor.offset === b.focus.offset) continue;
    const selection = $createRangeSelection();
    selection.anchor.set(b.anchor.nodeKey, b.anchor.offset, 'text');
    selection.focus.set(b.focus.nodeKey, b.focus.offset, 'text');
    $wrapSelectionInSuggestionMark(selection, spans[i].id, spans[i].source, spans[i].tooltip);
  }
}

type Pos = { nodeKey: NodeKey; offset: number };
type Boundary = { anchor: Pos; focus: Pos } | null;

// Finds all sentinel pairs, removes them, pre-splits text nodes at every
// span boundary, and returns node-aligned positions. Pre-splitting ensures
// $wrapSelectionInMarkNode's extract() never needs to fragment a TextNode,
// which avoids the node-displacement bug that scrambles later spans.
function $collectBoundaries(count: number): Boundary[] {
  const allHits: Array<{ nodeKey: NodeKey; offset: number; kind: 'start' | 'end' }> = [];
  for (const node of $nodesOfType(TextNode)) {
    const text = node.getTextContent();
    const key = node.getKey();
    let searchFrom = 0;
    while (searchFrom < text.length) {
      const si = text.indexOf(SENTINEL_START, searchFrom);
      const ei = text.indexOf(SENTINEL_END, searchFrom);
      if (si === -1 && ei === -1) break;

      const useSi = si !== -1 && (ei === -1 || si <= ei);
      if (useSi) {
        allHits.push({ nodeKey: key, offset: si, kind: 'start' });
        searchFrom = si + SENTINEL_START.length;
      } else {
        allHits.push({ nodeKey: key, offset: ei, kind: 'end' });
        searchFrom = ei + SENTINEL_END.length;
      }
    }
  }

  const starts: typeof allHits = [];
  const ends: typeof allHits = [];
  for (const h of allHits) {
    (h.kind === 'start' ? starts : ends).push(h);
  }

  if (starts.length < count || ends.length < count) {
    return Array(count).fill(null);
  }

  const removals = new Map<NodeKey, number[]>();
  for (const h of allHits) {
    let arr = removals.get(h.nodeKey);
    if (!arr) {
      arr = [];
      removals.set(h.nodeKey, arr);
    }
    arr.push(h.offset);
  }

  // Remove sentinels from every node, tracking how offsets shift.
  // Nodes that become empty keep a ZWS placeholder so their keys remain
  // valid; $removeAllSentinels cleans them up after wrapping.
  const sentinelOffsets = new Map<NodeKey, number[]>();
  const emptyNodes = new Set<NodeKey>();
  for (const [key, offsets] of removals) {
    offsets.sort((a, b) => a - b);
    sentinelOffsets.set(key, offsets);
    const node = $getNodeByKey(key);
    if (!$isTextNode(node)) continue;
    const text = node.getTextContent();
    let cleaned = '';
    let prev = 0;
    for (const off of offsets) {
      cleaned += text.substring(prev, off);
      prev = off + SENTINEL_START.length;
    }
    cleaned += text.substring(prev);
    if (cleaned === '') {
      emptyNodes.add(key);
      node.setTextContent('\u200B');
    } else {
      node.setTextContent(cleaned);
    }
  }

  function adjustOffset(nodeKey: NodeKey, origOffset: number): number {
    const offsets = sentinelOffsets.get(nodeKey);
    if (!offsets) return origOffset;
    let shift = 0;
    for (const off of offsets) {
      if (off < origOffset) shift += SENTINEL_START.length;
      else break;
    }
    return origOffset - shift;
  }

  function contentPos(hit: (typeof allHits)[0], side: 'start' | 'end'): Pos | null {
    const node = $getNodeByKey(hit.nodeKey);
    if (!node || !$isTextNode(node)) return null;

    if (emptyNodes.has(hit.nodeKey)) {
      if (side === 'start') {
        const next = node.getNextSibling();
        if (!next || !$isTextNode(next)) return null;
        return { nodeKey: next.getKey(), offset: 0 };
      }
      const prev = node.getPreviousSibling();
      if (!prev || !$isTextNode(prev)) return null;
      return { nodeKey: prev.getKey(), offset: prev.getTextContentSize() };
    }

    const adjusted = adjustOffset(hit.nodeKey, hit.offset);
    const len = node.getTextContentSize();
    if (side === 'start') {
      if (adjusted >= len) {
        const next = node.getNextSibling();
        if (!next || !$isTextNode(next)) return null;
        return { nodeKey: next.getKey(), offset: 0 };
      }
      return { nodeKey: hit.nodeKey, offset: adjusted };
    }
    return { nodeKey: hit.nodeKey, offset: Math.min(adjusted, len) };
  }

  const rawBoundaries: Boundary[] = [];
  for (let i = 0; i < count; i++) {
    const anchor = contentPos(starts[i], 'start');
    const focus = contentPos(ends[i], 'end');
    rawBoundaries.push(anchor && focus ? { anchor, focus } : null);
  }

  // Pre-split text nodes at every boundary offset so that extract() during
  // wrapping never fragments a node (which causes displacement).
  return $splitAtBoundaries(rawBoundaries);
}

function $splitAtBoundaries(boundaries: Boundary[]): Boundary[] {
  // Collect all split points grouped by node, with back-references to which
  // boundary positions need updating
  const splits = new Map<
    NodeKey,
    Array<{ offset: number; refs: Array<{ bIdx: number; field: 'anchor' | 'focus' }> }>
  >();

  for (let i = 0; i < boundaries.length; i++) {
    const b = boundaries[i];
    if (!b) continue;
    for (const field of ['anchor', 'focus'] as const) {
      const pos = b[field];
      if (pos.offset === 0) continue;
      const node = $getNodeByKey(pos.nodeKey);
      if (!node || !$isTextNode(node)) continue;
      if (pos.offset >= node.getTextContentSize()) continue;

      let entries = splits.get(pos.nodeKey);
      if (!entries) {
        entries = [];
        splits.set(pos.nodeKey, entries);
      }
      let entry = entries.find(e => e.offset === pos.offset);
      if (!entry) {
        entry = { offset: pos.offset, refs: [] };
        entries.push(entry);
      }
      entry.refs.push({ bIdx: i, field });
    }
  }

  for (const [nodeKey, entries] of splits) {
    const node = $getNodeByKey(nodeKey);
    if (!node || !$isTextNode(node)) continue;

    // Split from right to left so earlier offsets stay valid
    entries.sort((a, b) => b.offset - a.offset);
    for (const entry of entries) {
      const [, rightNode] = node.splitText(entry.offset);
      if (!rightNode) continue;
      const rightKey = rightNode.getKey();
      for (const ref of entry.refs) {
        const b = boundaries[ref.bIdx];
        if (!b) continue;
        b[ref.field] = { nodeKey: rightKey, offset: 0 };
      }

      // Also update any other entries/refs in the same node that had
      // offsets > this split point — they're now in rightNode
      for (let i = 0; i < boundaries.length; i++) {
        const ob = boundaries[i];
        if (!ob) continue;
        for (const f of ['anchor', 'focus'] as const) {
          if (ob[f].nodeKey === nodeKey && ob[f].offset > entry.offset) {
            ob[f] = { nodeKey: rightKey, offset: ob[f].offset - entry.offset };
          }
        }
      }
    }
  }

  return boundaries;
}

function $removeAllSentinels(): void {
  for (const node of $nodesOfType(TextNode)) {
    const text = node.getTextContent();
    if (text === '\u200B') {
      node.remove();
      continue;
    }
    if (!text.includes('\uFDD0') && !text.includes('\uFDD1')) continue;

    const cleaned = text.replace(/\uFDD0;|;\uFDD1|\uFDD0|\uFDD1/g, '');
    if (cleaned === '') {
      node.remove();
    } else {
      node.setTextContent(cleaned);
    }
  }
}

const MARKER_PATTERNS = ['***', '___', '**', '__', '~~', '*', '_'];

const SENTINEL_CONTENT_RE = /(\uFDD0;)([\s\S]*?)(;\uFDD1)/g;

const FORMAT_MARKER = /(\*{1,3}|_{1,3}|~~)/g;

function stripDeletionFormatting(processed: string, spans: SuggestionSpan[]): string {
  let spanIndex = 0;
  return processed.replace(
    SENTINEL_CONTENT_RE,
    (_match, open: string, content: string, close: string) => {
      const span = spans[spanIndex++];
      if (span?.source !== 'deletion') {
        return `${open}${content}${close}`;
      }
      const stripped = content.replace(FORMAT_MARKER, (marker, _group, offset) => {
        const before = content[offset - 1];
        const after = content[offset + marker.length];
        const alnum = /[a-zA-Z0-9]/;
        if (before && alnum.test(before) && after && alnum.test(after)) return marker;
        return '';
      });
      return `${open}${stripped}${close}`;
    },
  );
}

function $clearDeletionMarkFormatting(): void {
  for (const mark of $nodesOfType(SuggestionMarkNode)) {
    if ($sourceSuggestionMark(mark) !== 'deletion') continue;
    for (const child of mark.getChildren()) {
      if ($isTextNode(child) && child.getFormat() !== 0) {
        child.setFormat(0);
      }
    }
  }
}

function stripEdgeMarker(content: string, marker: string): string {
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const startMatch = new RegExp(`^([^a-zA-Z0-9]*)${escaped}`).exec(content);
  if (startMatch) {
    const rest = content.slice(startMatch[0].length);
    if (!rest.includes(marker)) {
      const prefix = startMatch[1];
      const afterMarker = content.slice(prefix.length + marker.length);
      return prefix + afterMarker;
    }
  }

  const endMatch = new RegExp(`${escaped}([^a-zA-Z0-9]*)$`).exec(content);
  if (endMatch) {
    const beforeMarker = content.slice(0, endMatch.index);
    if (!beforeMarker.includes(marker)) {
      const suffix = endMatch[1];
      return beforeMarker + suffix;
    }
  }

  return content;
}

function $stripOrphanedMarkersFromTree(): void {
  for (const node of $nodesOfType(TextNode)) {
    const text = node.getTextContent();
    let stripped = text;
    for (const marker of MARKER_PATTERNS) {
      stripped = stripEdgeMarker(stripped, marker);
    }
    if (stripped !== text) {
      if (stripped === '') {
        node.remove();
      } else {
        node.setTextContent(stripped);
      }
    }
  }
}

const DELETION_TAG_PREFIX = '<suggestion source="deletion">';
const LIST_PREFIX_PATTERN = /^\s*(?:[-*+]\s+|\d+\.\s+)?/;
const HEADING_PATTERN = /^#{1,6}\s+/;

function allChildrenAreDeletions(
  lines: string[],
  startIndex: number,
  isChildLine: (line: string) => boolean,
): { hasChildren: boolean; allDeleted: boolean } {
  let hasChildren = false;
  for (let j = startIndex; j < lines.length; j++) {
    const childLine = lines[j];
    if (childLine.trim() === '') continue;
    if (!isChildLine(childLine)) break;
    hasChildren = true;
    const stripped = childLine.replace(LIST_PREFIX_PATTERN, '');
    if (!stripped.startsWith(DELETION_TAG_PREFIX)) {
      return { hasChildren: true, allDeleted: false };
    }
  }
  return { hasChildren, allDeleted: true };
}

export function markOrphanParents(markdown: string): string {
  const lines = markdown.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.includes('<suggestion')) continue;

    const m = line.match(/^(\s*(?:[-*+]\s+|\d+\.\s+)|#{1,6}\s+|>\s+)/);
    if (!m) continue;

    const next = lines[i + 1];
    if (!next) continue;

    const nextStripped = next.replace(LIST_PREFIX_PATTERN, '');
    if (!nextStripped.startsWith(DELETION_TAG_PREFIX)) continue;

    const isBullet = /^\s*[-*+]\s+/.test(line);
    const isHeading = HEADING_PATTERN.test(line);

    if (isBullet) {
      const parentIndent = line.match(/^\s*/)![0].length;
      const nextIndent = next.match(/^\s*/)![0].length;
      if (nextIndent <= parentIndent) continue;

      const result = allChildrenAreDeletions(lines, i + 1, childLine => {
        const childIndent = childLine.match(/^\s*/)![0].length;
        return childIndent > parentIndent;
      });
      if (!result.allDeleted) continue;
    } else if (isHeading) {
      const result = allChildrenAreDeletions(
        lines,
        i + 1,
        childLine => !HEADING_PATTERN.test(childLine),
      );
      if (!result.hasChildren || !result.allDeleted) continue;
    }

    const prefix = m[1];
    lines[i] = `${prefix}<suggestion source="deletion">${line.slice(prefix.length)}</suggestion>`;
  }
  return lines.join('\n');
}

// Standalone \newpage lines are deliberately excluded: they must stay on the
// collapse path so extractPageBreaksFromSuggestions can split them out.
const BLOCK_STRUCTURE_LINE_RE = /^\s*(?:#{1,6}\s|[-*+]\s|\d+\.\s|>\s)/;

function contentHasBlockStructure(lines: string[]): boolean {
  return lines.some(line => BLOCK_STRUCTURE_LINE_RE.test(line));
}

export function normalizeInlineSuggestions(markdown: string): string {
  return markdown.replace(
    /<suggestion(?: source="([^"]*)")?>([\s\S]*?)<\/suggestion>/g,
    (match, source: string | undefined, content: string, offset: number, whole: string) => {
      const sourceAttr = source ? ` source="${source}"` : '';

      const lines = content.trim().split('\n');
      const isTableRows = lines.every(line => TABLE_ROW_REG_EXP.test(line.trim()));

      const tagStartsLine = offset === 0 || whole[offset - 1] === '\n';
      const tagEndsLine = (whole[offset + match.length] ?? '\n') === '\n';
      if (!isTableRows && tagStartsLine && tagEndsLine && contentHasBlockStructure(lines)) {
        return `<suggestion${sourceAttr}>\n${content.trim()}\n</suggestion>`;
      }

      if (isTableRows) {
        return lines
          .map(line => {
            const trimmed = line.trim();
            if (TABLE_ROW_DIVIDER_REG_EXP.test(trimmed)) return trimmed;
            const inner = trimmed.slice(1, trimmed.lastIndexOf('|'));
            const cells = inner.split('|');
            const wrapped = cells.map((cell: string) => {
              const text = cell.trim();
              return ` <suggestion${sourceAttr}>${text || '\u200b'}</suggestion> `;
            });
            return `|${wrapped.join('|')}|`;
          })
          .join('\n');
      }

      const normalizedContent = content.replace(/^\n+|\n+$/g, '').replace(/\n/g, '<br/>');
      return `<suggestion${sourceAttr}>${normalizedContent}</suggestion>`;
    },
  );
}

const FULL_LINE_SUGGESTION_RE = /^<suggestion([^>]*)>(.*?)<\/suggestion>\s*$/;
const BR_TAG_RE = /<br\s*\/?>/;

// Splits any single-line suggestion whose content includes a standalone
// \newpage into the multi-line block form, so the page break reaches the
// block SuggestionNode transformer instead of becoming literal mark text.
export function extractPageBreaksFromSuggestions(markdown: string): string {
  return markdown
    .split('\n')
    .map(line => {
      const match = line.match(FULL_LINE_SUGGESTION_RE);
      if (!match) return line;

      const [, attrs, content] = match;
      const segments = content.split(BR_TAG_RE);
      if (!segments.some(segment => PAGE_BREAK_LINE_RE.test(segment))) return line;

      const output: string[] = [];
      let pending: string[] = [];
      const flushPending = () => {
        if (pending.length === 0) return;
        output.push(`<suggestion${attrs}>${pending.join('<br/>')}</suggestion>`);
        pending = [];
      };

      for (const segment of segments) {
        if (PAGE_BREAK_LINE_RE.test(segment)) {
          flushPending();
          output.push(`<suggestion${attrs}>`, PAGE_BREAK_MARKER, '</suggestion>');
        } else if (segment.trim() !== '') {
          pending.push(segment);
        }
      }
      flushPending();
      return output.join('\n');
    })
    .join('\n');
}

export function $acceptSuggestions(): void {
  for (const node of $nodesOfType(SuggestionMarkNode)) {
    const source = $sourceSuggestionMark(node);
    if (source === 'inline') {
      $setSuggestionMarkSource(node, 'default');
    } else if (source === 'deletion') {
      $removeSuggestionMarkWithContent(node);
    }
  }
  for (const node of $nodesOfType(SuggestionNode)) {
    const source = $sourceSuggestion(node);
    if (source === 'inline') {
      $setSuggestionSource(node, 'default');
    } else if (source === 'deletion') {
      $removeSuggestionWithContent(node);
    }
  }
  $removeEmptyListItems($getRoot());
  $removeEmptyTableRows();
  $removeEmptyTableColumns();
}

export function $discardSuggestions(): void {
  for (const node of $nodesOfType(SuggestionMarkNode)) {
    const source = $sourceSuggestionMark(node);
    if (source === 'inline') {
      $removeSuggestionMarkWithContent(node);
    } else if (source === 'deletion') {
      $dismissSuggestionMark(node);
    }
  }
  for (const node of $nodesOfType(SuggestionNode)) {
    const source = $sourceSuggestion(node);
    if (source === 'inline') {
      $removeSuggestionWithContent(node);
    } else if (source === 'deletion') {
      $dismissSuggestion(node);
    }
  }
}
